

import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Import our single source of truth for post-login colors
import colors from './colors';

// Import the builder functions for each part of the theme.
import getPalette from './palette';
import getTypography from './typography';
import getComponentOverrides from './compStyleOverride';

// Import the custom shadows function for the pre-login theme.
import shadows from './shadows';

// --- THIS IS THE DEFINITIVE FIX FOR THE SHADOWS ERROR ---
// 1. We create a temporary, default MUI theme just once to access its properties.
const defaultMuiTheme = createTheme();
// 2. We capture its standard 'shadows' array to use as a fallback.
const defaultMuiShadows = defaultMuiTheme.shadows;
// --- END OF FIX ---

/**
 * Creates a unified theme for the entire application.
 * This function can generate either the 'preLogin' or 'postLogin' theme.
 *
 * @param {object} config - The configuration object for the theme.
 * @param {'preLogin' | 'postLogin'} config.mode - The primary mode to determine the theme's structure.
 * @param {'light' | 'dark'} [config.colorMode='light'] - The color mode, primarily for the preLogin theme.
 * @param {object} [config.customization={}] - User-specific customizations for the postLogin theme.
 * @returns A complete Material-UI theme object.
 */
export function createUnifiedTheme({ mode, colorMode = 'light', customization = {} }) {
    // The themeOption object is the central "blueprint" for the postLogin theme.
    // It assigns semantic roles to the raw color values.
    const themeOption = {
        colors: colors,
        heading: colors.grey900,
        paper: colors.paper,
        backgroundDefault: colors.paper,
        background: colors.primaryLight,
        darkTextPrimary: colors.brownMain,
        darkTextSecondary: colors.brownLight,
        textDark: colors.brownMain,
        menuSelected: colors.primaryDark,
        menuSelectedBack: colors.primaryLight,
        divider: colors.grey200,
        customization: customization,
    };

    // This is the main configuration object that will be passed to MUI's createTheme.
    const themeConfig = {
        direction: 'ltr',
        palette: getPalette({ themeOption, mode, colorMode }),
        typography: getTypography({ themeOption, mode }),
        components: getComponentOverrides({ themeOption, mode, colorMode }),
        // The 'shadows' property is now ALWAYS defined, guaranteeing a complete theme.
        shadows: mode === 'preLogin' ? shadows(colorMode) : defaultMuiShadows,
        breakpoints: {
            values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
        },
    };

    // --- THEME-SPECIFIC LOGIC ---
    // The preLogin theme had a specific zIndex configuration. We add it here
    // conditionally so it doesn't affect the postLogin theme.
    if (mode === 'preLogin') {
        themeConfig.zIndex = {
            appBar: 1200,
            drawer: 1300,
        };
        // The redundant shadows line that was here has been correctly moved into the main themeConfig.
    }

    // Create the base theme from our configuration.
    const theme = createTheme(themeConfig);

    // The preLogin theme also used responsiveFontSizes. We apply it here
    // conditionally to perfectly match the original behavior.
    if (mode === 'preLogin') {
        return responsiveFontSizes(theme);
    }

    return theme;
}

// We also need to export it as a default for compatibility with some of your original files.
export default createUnifiedTheme;